import Link from '@/lib/nav/link';
import { useRouter, useSearchParams } from '@/lib/nav/navigation';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { SignInButton } from '@/lib/auth/client-auth';
import { Authenticated, AuthLoading, Unauthenticated, useQuery } from '@/lib/data/convex-bypass';
import type { FunctionReturnType } from 'convex/server';
import { FlightIcon as Plane } from '@/components/icons/line/flight';
import { api } from '@convex/_generated/api';
import { RoamLoading } from '@/components/branding/RoamLoading';
import { isLaunchSplashVisible } from '@/lib/pwa/app-launch';
import { TRIP_LAUNCH_LOADING_LABEL } from '@/lib/trip/tripLaunchLoading';
import { isStandaloneDisplay } from '@/lib/install-platform';
import { withTripViewQuery } from '@/lib/trip/active-trip-view';

type Trip = FunctionReturnType<typeof api.trips.listMine>[number];

function choosePostLoginDestination(trips: Trip[], lastActiveTripId: string | null) {
  const activeTrips = trips.filter((trip) => trip.status !== 'archived');

  if (activeTrips.length === 0) {
    return trips.length > 0 ? '/trips' : '/new-trip';
  }

  // Prefer the trip the user was last working on, if it's still available.
  if (lastActiveTripId) {
    const remembered = activeTrips.find((trip) => trip._id === lastActiveTripId);
    if (remembered) return `/trips/${remembered._id}`;
  }

  const today = new Date().toISOString().slice(0, 10);
  const upcomingTrips = activeTrips
    .filter((trip) => trip.startDate && trip.startDate >= today)
    .sort((a, b) => {
      const dateComparison = a.startDate!.localeCompare(b.startDate!);
      return dateComparison || b.updatedAt - a.updatedAt;
    });

  const target =
    upcomingTrips[0] ?? activeTrips.sort((a, b) => b.updatedAt - a.updatedAt)[0] ?? null;

  return target ? `/trips/${target._id}` : '/new-trip';
}

function LaunchLoading({ label }: { label: string }) {
  if (isLaunchSplashVisible()) return null;
  return <RoamLoading label={label} />;
}

function StandaloneSignInRedirect() {
  const location = useLocation();
  const next = `${location.pathname}${location.search}`;
  return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
}

export function ActiveTripRedirectClient() {
  const standalone = isStandaloneDisplay();

  return (
    <>
      <AuthLoading>
        <LaunchLoading label={TRIP_LAUNCH_LOADING_LABEL} />
      </AuthLoading>

      <Unauthenticated>
        {standalone ? (
          <StandaloneSignInRedirect />
        ) : (
          <main className="grid min-h-dvh place-items-center bg-[#f7f3ef] px-4 text-[#181715]">
            <section className="w-full max-w-md rounded-lg border border-black/10 bg-white p-6 text-center shadow-sm">
              <Plane className="mx-auto h-6 w-6 text-[#d45c45]" />
              <h1 className="mt-3 font-display text-2xl font-bold">Sign in to continue planning</h1>
              <p className="mt-2 text-sm leading-6 text-black/60">
                Roam will take you back to your active trip after you sign in.
              </p>
              <div className="mt-5 flex justify-center gap-2">
                <SignInButton mode="modal">
                  <button className="inline-flex h-10 items-center justify-center rounded-full bg-[#181715] px-4 text-sm font-bold text-white transition hover:bg-black">
                    Sign in
                  </button>
                </SignInButton>
                <Link
                  href="/explore"
                  className="inline-flex h-10 items-center justify-center rounded-full border border-black/10 px-4 text-sm font-bold text-black/65 transition hover:border-black/25"
                >
                  Explore
                </Link>
              </div>
            </section>
          </main>
        )}
      </Unauthenticated>

      <Authenticated>
        <ActiveTripRedirect />
      </Authenticated>
    </>
  );
}

function ActiveTripRedirect() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const trips = useQuery(api.trips.listMine);
  const navState = useQuery(api.users.getNavState);
  const view = searchParams.get('view');

  useEffect(() => {
    if (!trips || navState === undefined) return;
    const destination = withTripViewQuery(
      choosePostLoginDestination(trips, navState?.lastActiveTripId ?? null),
      view,
    );
    router.replace(destination);
  }, [router, trips, navState, view]);

  return <LaunchLoading label={TRIP_LAUNCH_LOADING_LABEL} />;
}
